import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { Save, Loader2, Home, Image, Type, Link2, Quote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Homepage = () => {
  const { getAuthHeaders } = useAdmin();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_title_1: '',
    hero_title_2: '',
    hero_subtitle: '',
    hero_image: '',
    hero_cta_1_text: '',
    hero_cta_1_link: '',
    hero_cta_2_text: '',
    hero_cta_2_link: '',
    about_title: '',
    about_subtitle: '',
    about_text_1: '',
    about_text_2: '',
    about_quote: '',
    about_image: '',
    feature_1_title: '',
    feature_1_desc: '',
    feature_2_title: '',
    feature_2_desc: '',
    feature_3_title: '',
    feature_3_desc: '',
    catalog_title: '',
    catalog_subtitle: '',
    catalog_text: '',
    catalog_cta_1: '',
    catalog_cta_2: ''
  });

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/homepage`, {
        headers: getAuthHeaders()
      });
      setContent(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar conteúdo da homepage',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/homepage`, content, {
        headers: getAuthHeaders()
      });
      toast({ title: 'Conteúdo guardado com sucesso!' });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao guardar conteúdo',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#cc8b3a] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Editar Homepage</h1>
          <p className="text-white/60">Personalize o conteúdo da página inicial</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black font-bold"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Alterações
        </Button>
      </div>

      <Tabs defaultValue="hero" className="w-full">
        <TabsList className="bg-[#2a2520] mb-6">
          <TabsTrigger value="hero" className="data-[state=active]:bg-[#cc8b3a] data-[state=active]:text-black">
            <Home className="w-4 h-4 mr-2" />
            Hero
          </TabsTrigger>
          <TabsTrigger value="about" className="data-[state=active]:bg-[#cc8b3a] data-[state=active]:text-black">
            <Type className="w-4 h-4 mr-2" />
            Sobre Nós
          </TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-[#cc8b3a] data-[state=active]:text-black">
            <Quote className="w-4 h-4 mr-2" />
            Destaques
          </TabsTrigger>
          <TabsTrigger value="catalog" className="data-[state=active]:bg-[#cc8b3a] data-[state=active]:text-black">
            <Link2 className="w-4 h-4 mr-2" />
            Catálogo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hero">
          <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Home className="w-5 h-5 mr-2 text-[#cc8b3a]" />
                Secção Hero (Banner Principal)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white">Título Linha 1</Label>
                  <Input
                    value={content.hero_title_1}
                    onChange={(e) => handleChange('hero_title_1', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Respeito pelo tempo"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Título Linha 2</Label>
                  <Input
                    value={content.hero_title_2}
                    onChange={(e) => handleChange('hero_title_2', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Paixão pela carne"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Subtítulo</Label>
                <Input
                  value={content.hero_subtitle}
                  onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  placeholder="Fábrica artesanal de carnes defumadas"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Imagem de Fundo (URL)</Label>
                <Input
                  value={content.hero_image}
                  onChange={(e) => handleChange('hero_image', e.target.value)}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  placeholder="https://..."
                />
                {content.hero_image && (
                  <img src={content.hero_image} alt="Preview" className="mt-2 max-h-32 rounded" />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-black/30 rounded-lg">
                  <h4 className="text-[#cc8b3a] font-semibold">Botão Principal</h4>
                  <div className="space-y-2">
                    <Label className="text-white">Texto</Label>
                    <Input
                      value={content.hero_cta_1_text}
                      onChange={(e) => handleChange('hero_cta_1_text', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="Ver Produtos"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Link</Label>
                    <Input
                      value={content.hero_cta_1_link}
                      onChange={(e) => handleChange('hero_cta_1_link', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="/produtos"
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-black/30 rounded-lg">
                  <h4 className="text-[#cc8b3a] font-semibold">Botão Secundário</h4>
                  <div className="space-y-2">
                    <Label className="text-white">Texto</Label>
                    <Input
                      value={content.hero_cta_2_text}
                      onChange={(e) => handleChange('hero_cta_2_text', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="Quem Somos"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Link</Label>
                    <Input
                      value={content.hero_cta_2_link}
                      onChange={(e) => handleChange('hero_cta_2_link', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="#quem-somos"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="about">
          <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Type className="w-5 h-5 mr-2 text-[#cc8b3a]" />
                Secção "Quem Somos"
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white">Etiqueta</Label>
                  <Input
                    value={content.about_subtitle}
                    onChange={(e) => handleChange('about_subtitle', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Sobre Nós"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Título</Label>
                  <Input
                    value={content.about_title}
                    onChange={(e) => handleChange('about_title', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Quem Somos"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Parágrafo 1</Label>
                <Textarea
                  value={content.about_text_1}
                  onChange={(e) => handleChange('about_text_1', e.target.value)}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  rows={4}
                  placeholder="Na Santa Carne, o tempo é o nosso maior ingrediente..."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Parágrafo 2</Label>
                <Textarea
                  value={content.about_text_2}
                  onChange={(e) => handleChange('about_text_2', e.target.value)}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  rows={4}
                  placeholder="Unimos tradição portuguesa, alma brasileira e técnica americana..."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Citação</Label>
                <Input
                  value={content.about_quote}
                  onChange={(e) => handleChange('about_quote', e.target.value)}
                  className="bg-black border-[#cc8b3a]/30 text-white italic"
                  placeholder="Santa Carne é sabor com identidade..."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Imagem (URL)</Label>
                <Input
                  value={content.about_image}
                  onChange={(e) => handleChange('about_image', e.target.value)}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  placeholder="https://..."
                />
                {content.about_image && (
                  <img src={content.about_image} alt="Preview" className="mt-2 max-h-32 rounded" />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features">
          <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Quote className="w-5 h-5 mr-2 text-[#cc8b3a]" />
                Cards de Destaques
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4 p-4 bg-black/30 rounded-lg">
                  <h4 className="text-[#cc8b3a] font-semibold">Destaque 1</h4>
                  <div className="space-y-2">
                    <Label className="text-white">Título</Label>
                    <Input
                      value={content.feature_1_title}
                      onChange={(e) => handleChange('feature_1_title', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="Premium"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Descrição</Label>
                    <Input
                      value={content.feature_1_desc}
                      onChange={(e) => handleChange('feature_1_desc', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="Cortes selecionados"
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-black/30 rounded-lg">
                  <h4 className="text-[#cc8b3a] font-semibold">Destaque 2</h4>
                  <div className="space-y-2">
                    <Label className="text-white">Título</Label>
                    <Input
                      value={content.feature_2_title}
                      onChange={(e) => handleChange('feature_2_title', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="Artesanal"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Descrição</Label>
                    <Input
                      value={content.feature_2_desc}
                      onChange={(e) => handleChange('feature_2_desc', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="Defumação lenta"
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-black/30 rounded-lg">
                  <h4 className="text-[#cc8b3a] font-semibold">Destaque 3</h4>
                  <div className="space-y-2">
                    <Label className="text-white">Título</Label>
                    <Input
                      value={content.feature_3_title}
                      onChange={(e) => handleChange('feature_3_title', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="Parceiros"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Descrição</Label>
                    <Input
                      value={content.feature_3_desc}
                      onChange={(e) => handleChange('feature_3_desc', e.target.value)}
                      className="bg-black border-[#cc8b3a]/30 text-white"
                      placeholder="B2B & Eventos"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="catalog">
          <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Link2 className="w-5 h-5 mr-2 text-[#cc8b3a]" />
                Secção Catálogo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white">Etiqueta</Label>
                  <Input
                    value={content.catalog_subtitle}
                    onChange={(e) => handleChange('catalog_subtitle', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Download"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Título</Label>
                  <Input
                    value={content.catalog_title}
                    onChange={(e) => handleChange('catalog_title', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Catálogo Completo"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Texto</Label>
                <Textarea
                  value={content.catalog_text}
                  onChange={(e) => handleChange('catalog_text', e.target.value)}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  rows={3}
                  placeholder="Para restaurantes, talhos e eventos..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white">Botão 1 (Texto)</Label>
                  <Input
                    value={content.catalog_cta_1}
                    onChange={(e) => handleChange('catalog_cta_1', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Download PDF"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Botão 2 (Texto)</Label>
                  <Input
                    value={content.catalog_cta_2}
                    onChange={(e) => handleChange('catalog_cta_2', e.target.value)}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="Ver Catálogo"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Homepage;
